import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { AgentLayout } from "@/components/AgentLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  CircleNotch, Copy, Gift, ShareNetwork, Users, CheckCircle, Clock,
  EnvelopeSimple, ChatTeardropDots,
} from "@phosphor-icons/react";
import { toast } from "sonner";

const STATUS_PILL = {
  pending: "bg-amber-500/10 text-amber-300 border-amber-400/30",
  qualified: "bg-emerald-500/15 text-emerald-300 border-emerald-400/40",
};

const BONUS_PILL = {
  earned: "bg-[#D4AF37]/15 text-[#D4AF37] border-[#D4AF37]/40",
  paid: "bg-emerald-500/15 text-emerald-300 border-emerald-400/40",
  pending: "bg-white/5 text-white/50 border-white/10",
};

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
  } catch { return "—"; }
}
function fmtMoney(n) {
  if (n == null || isNaN(n)) return "$0";
  return "$" + Number(n).toLocaleString("en-US", { maximumFractionDigits: 0 });
}

export default function Referrals() {
  const [data, setData] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/agent/referrals");
        setData(data);
      } catch {
        setData({ error: true });
      }
    })();
  }, []);

  const copy = (text, label = "Copied") => {
    navigator.clipboard.writeText(text);
    toast.success(label);
  };

  const shareNative = async () => {
    if (!data?.share_url) return;
    try {
      if (navigator.share) {
        await navigator.share({
          title: "Join Best Time Travel & Tours",
          text: `Use my referral code ${data.referral_code} to join Best Time Travel & Tours.`,
          url: data.share_url,
        });
      } else {
        copy(data.share_url, "Share link copied");
      }
    } catch { /* user cancelled */ }
  };

  const emailShare = () => {
    if (!data?.share_url) return;
    const subject = encodeURIComponent("Join me at Best Time Travel & Tours");
    const body = encodeURIComponent(
      `I'd like to invite you to join Best Time Travel & Tours as an agent.\n\nUse my referral code: ${data.referral_code}\nOr sign up directly: ${data.share_url}\n\nLooking forward to working with you!`
    );
    window.location.href = `mailto:?subject=${subject}&body=${body}`;
  };

  const smsShare = () => {
    if (!data?.share_url) return;
    const body = encodeURIComponent(
      `Join me at Best Time Travel & Tours — use referral code ${data.referral_code}: ${data.share_url}`
    );
    window.location.href = `sms:?&body=${body}`;
  };

  if (data === null) {
    return (
      <AgentLayout>
        <div className="py-32 flex justify-center">
          <CircleNotch size={32} className="text-[#D4AF37] animate-spin" />
        </div>
      </AgentLayout>
    );
  }
  if (data?.error) {
    return (
      <AgentLayout>
        <div className="border border-dashed border-red-400/30 rounded-lg p-12 text-center text-red-300/80 font-sans">
          Could not load your referrals. Please try again.
        </div>
      </AgentLayout>
    );
  }

  const summary = data.summary || {};
  const rows = data.rows || [];

  return (
    <AgentLayout>
      <div className="mb-10 animate-fade-up" data-testid="referrals-page">
        <p className="text-[11px] uppercase tracking-[0.35em] text-[#D4AF37] mb-3 font-sans">Refer & Earn</p>
        <h1 className="font-serif text-5xl sm:text-6xl tracking-tight text-white mb-3">
          Your <span className="italic text-[#D4AF37]">referrals</span>
        </h1>
        <p className="text-white/60 text-sm max-w-xl font-sans">
          Earn <strong className="text-[#D4AF37]">$25</strong> the moment a new agent joins with your code, plus another
          <strong className="text-[#D4AF37]"> $25</strong> when they confirm their first booking — a total of <strong>$50</strong> per qualified referral.
        </p>
      </div>

      {/* Share Card */}
      <div className="border border-[#D4AF37]/40 bg-gradient-to-br from-[#D4AF37]/15 via-[#D4AF37]/5 to-transparent rounded-xl p-6 sm:p-8 mb-10" data-testid="referral-share-card">
        <div className="flex items-start gap-4 mb-5">
          <div className="w-12 h-12 rounded-full bg-[#D4AF37]/20 flex items-center justify-center shrink-0">
            <Gift size={22} weight="duotone" className="text-[#D4AF37]" />
          </div>
          <div>
            <p className="text-[11px] uppercase tracking-[0.3em] text-[#D4AF37] font-sans">Your code</p>
            <p className="font-serif text-4xl text-white tracking-tight" data-testid="referral-code-display">
              {data.referral_code}
            </p>
          </div>
          <Button
            onClick={() => copy(data.referral_code, "Code copied")}
            variant="ghost"
            size="sm"
            className="ml-auto text-white/60 hover:text-[#D4AF37] hover:bg-[#D4AF37]/10"
            data-testid="copy-referral-code-btn"
          >
            <Copy size={16} weight="duotone" />
          </Button>
        </div>

        <div className="space-y-4">
          <div>
            <p className="text-[10px] uppercase tracking-[0.3em] text-white/50 font-sans mb-2">Share link</p>
            <div className="flex flex-col sm:flex-row gap-2">
              <Input
                value={data.share_url || ""}
                readOnly
                className="bg-black/40 border-white/10 text-white font-mono text-xs flex-1"
                data-testid="referral-share-link"
                onFocus={(e) => e.target.select()}
              />
              <Button
                onClick={() => copy(data.share_url, "Link copied")}
                className="bg-[#D4AF37] hover:bg-[#F3C853] text-black h-10 text-xs uppercase tracking-widest"
                data-testid="copy-referral-link-btn"
              >
                <Copy size={14} weight="duotone" className="mr-1.5" /> Copy
              </Button>
            </div>
          </div>

          <div className="flex flex-wrap gap-2 pt-1">
            <Button onClick={shareNative} variant="outline" size="sm"
              className="border-white/20 bg-transparent text-white/80 hover:bg-white/5 hover:text-[#D4AF37]"
              data-testid="referral-share-btn"
            >
              <ShareNetwork size={14} weight="duotone" className="mr-1.5" /> Share
            </Button>
            <Button onClick={emailShare} variant="outline" size="sm"
              className="border-white/20 bg-transparent text-white/80 hover:bg-white/5 hover:text-[#D4AF37]"
              data-testid="referral-email-btn"
            >
              <EnvelopeSimple size={14} weight="duotone" className="mr-1.5" /> Email
            </Button>
            <Button onClick={smsShare} variant="outline" size="sm"
              className="border-white/20 bg-transparent text-white/80 hover:bg-white/5 hover:text-[#D4AF37]"
              data-testid="referral-sms-btn"
            >
              <ChatTeardropDots size={14} weight="duotone" className="mr-1.5" /> Text
            </Button>
          </div>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-10">
        <StatCard
          label="Total referrals"
          value={summary.total_referrals || 0}
          sub={`${summary.qualified_referrals || 0} qualified · ${summary.pending_referrals || 0} pending`}
          icon={Users}
          testId="stat-total-referrals"
        />
        <StatCard
          label="Earned (pending payout)"
          value={fmtMoney(summary.pending_total)}
          sub="Awaiting admin payment"
          icon={Clock}
          gold
          testId="stat-pending-total"
        />
        <StatCard
          label="Paid out"
          value={fmtMoney(summary.paid_total)}
          sub="Bonuses received"
          icon={CheckCircle}
          testId="stat-paid-total"
        />
        <StatCard
          label="Lifetime earned"
          value={fmtMoney(summary.earned_total)}
          sub="Across all referrals"
          icon={Gift}
          testId="stat-lifetime-earned"
        />
      </div>

      {/* Table */}
      <div className="mb-4">
        <h2 className="font-serif text-2xl text-white">Referred agents</h2>
      </div>

      {rows.length === 0 ? (
        <div className="border border-dashed border-white/10 rounded-lg p-16 text-center font-sans" data-testid="referrals-empty">
          <Gift size={32} weight="duotone" className="text-white/30 mx-auto mb-3" />
          <p className="text-white/60 text-sm mb-1">No referrals yet</p>
          <p className="text-white/40 text-xs">Share your code above to earn $50 per qualified referral.</p>
        </div>
      ) : (
        <div className="border border-white/10 rounded-lg overflow-hidden overflow-x-auto" data-testid="referrals-table">
          <table className="w-full text-sm">
            <thead className="bg-black/40 text-[10px] uppercase tracking-widest text-white/50">
              <tr>
                <th className="text-left px-4 py-3 font-sans">Agent</th>
                <th className="text-left px-4 py-3 font-sans">Joined</th>
                <th className="text-left px-4 py-3 font-sans">Status</th>
                <th className="text-left px-4 py-3 font-sans">Signup bonus</th>
                <th className="text-left px-4 py-3 font-sans">Booking bonus</th>
                <th className="text-right px-4 py-3 font-sans">Total</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => {
                const total = (r.signup_bonus_amount || 0) + (r.booking_bonus_amount || 0);
                return (
                  <tr key={r.id} className="border-t border-white/5 hover:bg-white/[0.02]" data-testid={`referral-row-${r.id}`}>
                    <td className="px-4 py-3">
                      <p className="text-white">{r.referred_name || r.referred_email?.split("@")[0]}</p>
                      <p className="text-[11px] text-white/40 font-mono">{r.referred_email}</p>
                    </td>
                    <td className="px-4 py-3 text-white/70 whitespace-nowrap">{fmtDate(r.date_joined)}</td>
                    <td className="px-4 py-3">
                      <span className={`inline-block px-2 py-0.5 rounded-full text-[10px] uppercase tracking-widest border font-sans ${STATUS_PILL[r.status] || STATUS_PILL.pending}`}>
                        {r.status === "qualified" ? "Qualified" : "Pending booking"}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <BonusPill kind="signup" amount={r.signup_bonus_amount} status={r.signup_bonus_status} />
                    </td>
                    <td className="px-4 py-3">
                      <BonusPill kind="booking" amount={r.booking_bonus_amount} status={r.booking_bonus_status} />
                    </td>
                    <td className="px-4 py-3 text-right font-mono text-white/90">{fmtMoney(total)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <p className="text-[11px] text-white/40 italic mt-6">
        Bonuses are paid out manually by an administrator after a referral qualifies.
      </p>
    </AgentLayout>
  );
}

function StatCard({ label, value, sub, icon: Icon, gold, testId }) {
  return (
    <div
      className={`rounded-lg p-5 border ${gold
        ? "border-[#D4AF37]/40 bg-gradient-to-br from-[#D4AF37]/10 to-transparent"
        : "border-white/10 bg-ink-surface"}`}
      data-testid={testId}
    >
      <div className="flex items-center gap-2 mb-2">
        <Icon size={14} weight="duotone" className={gold ? "text-[#D4AF37]" : "text-white/50"} />
        <p className={`text-[10px] uppercase tracking-[0.3em] font-sans ${gold ? "text-[#D4AF37]" : "text-white/50"}`}>
          {label}
        </p>
      </div>
      <p className="font-serif text-3xl text-white">{value}</p>
      <p className="text-xs text-white/50 font-sans mt-1">{sub}</p>
    </div>
  );
}

function BonusPill({ kind, amount, status }) {
  const cls = BONUS_PILL[status] || BONUS_PILL.pending;
  const label = status === "paid" ? "Paid" : status === "earned" ? "Earned" : "Pending";
  return (
    <div className="flex items-center gap-2">
      <span className="text-white/90 font-mono">{fmtMoney(amount)}</span>
      <span className={`inline-block px-2 py-0.5 rounded-full text-[10px] uppercase tracking-widest border font-sans ${cls}`}>
        {label}
      </span>
    </div>
  );
}
